import { Briefcase } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';

import { createAssignmentActionAtom } from '@/stores';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AssignmentPriority } from '@/types';
import { useForm } from 'react-hook-form';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { fetchSourcesActionAtom, sourcesAtom } from '@/stores/sourcesStore';
import { fetchTipsActionAtom, tipsAtom } from '@/stores/tipsStore';
import { Button } from '@/components/ui/button';

interface NewAssignmentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type NewAssignmentFormValues = {
  title: string;
  description?: string;
  priority: AssignmentPriority;
  sourceId: string;
  tipId: string;
};

export function NewAssignmentDialog({
  open,
  onOpenChange,
}: NewAssignmentModalProps) {
  const createAssignment = useSetAtom(createAssignmentActionAtom);
  const sources = useAtomValue(sourcesAtom);
  const tips = useAtomValue(tipsAtom);
  const fetchSources = useSetAtom(fetchSourcesActionAtom);
  const fetchTips = useSetAtom(fetchTipsActionAtom);
  const hasFetchedRef = useRef(false);
  const form = useForm<NewAssignmentFormValues>({
    defaultValues: {
      title: '',
      description: '',
      priority: 'medium',
      sourceId: '',
      tipId: '',
    },
  });

  useEffect(() => {
    if (!open || hasFetchedRef.current) {
      return;
    }
    hasFetchedRef.current = true;

    Promise.all([fetchSources(), fetchTips()]).catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onSubmit = async (values: NewAssignmentFormValues) => {
    await createAssignment(values);
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[500px]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <Briefcase className='h-5 w-5' />
            New Assignment
          </DialogTitle>
          <DialogDescription>
            Fill out the fields below to create a new assignment.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
            <FormField
              control={form.control}
              name='title'
              rules={{ required: 'Title is required' }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input
                      placeholder='Investigate product launch…'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='description'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea rows={4} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='priority'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Priority</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder='Select priority' />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value='urgent'>Urgent</SelectItem>
                      <SelectItem value='high'>High</SelectItem>
                      <SelectItem value='medium'>Medium</SelectItem>
                      <SelectItem value='low'>Low</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='sourceId'
              rules={{ required: 'Source is required' }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Source</FormLabel>
                  <FormControl>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger>
                        <SelectValue placeholder='Select source' />
                      </SelectTrigger>
                      <SelectContent className='w-[var(--radix-select-trigger-width)]'>
                        {sources.map(source => (
                          <SelectItem
                            key={source.id}
                            value={source.id}
                            className='truncate'
                          >
                            {source.firstName} {source.lastName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='tipId'
              rules={{ required: 'Tip is required' }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tip</FormLabel>
                  <FormControl>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger className='w-full'>
                        <SelectValue placeholder='Select tip' />
                      </SelectTrigger>
                      <SelectContent className='w-[var(--radix-select-trigger-width)]'>
                        {tips.map(tip => (
                          <SelectItem
                            key={tip.id}
                            value={tip.id}
                            className='truncate'
                          >
                            {tip.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className='flex justify-end gap-2'>
              <Button
                type='button'
                variant='outline'
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type='submit'>Create</Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
